import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { RefreshableResult, successfulResult, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { filter, map, shareReplay, switchMap, take, tap, combineLatest } from 'rxjs';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { ActivatedRoute } from '@angular/router';
import { MessageService, selectedThread } from '@lib/messages/message.service';
import { AsyncPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { ComponentsModule, DialogService, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { getDisplayName } from '@lib/api/models/person';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { ChangeParticipantPresence, ChangeParticipantPresenceResponse, Message, Thread } from '@lib/api/models/message';
import { ThreadMessagesComponent } from '../../components/message/thread-messages/thread-messages.component';
import { ThreadInboxComponent } from '../../components/message/thread-inbox/thread-inbox.component';
import { CreateMessageComponent } from '../../components/message/create-message/create-message.component';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessagesActionsComponent } from "../../components/message/messages-actions/messages-actions.component";

@Component({
    selector: 'app-messages-index-page',
    imports: [
    AsyncPipe,
    ComponentsModule,
    HeaderComponent,
    ThreadMessagesComponent,
    ThreadInboxComponent,
    ErrorPanelComponent,
    MessagesActionsComponent
],
    templateUrl: './messages-index-page.component.html',
    standalone: true,
    styleUrl: './messages-index-page.component.scss'
})
export class MessagesIndexPageComponent implements OnInit {

    protected readonly auth = inject(AuthenticationService);
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _dialog = inject(DialogService);
    private readonly _messageService = inject(MessageService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _destroyRef = inject(DestroyRef);

    protected readonly getDisplayName = getDisplayName;
    protected readonly selectedThread = selectedThread;

    selectedThreadIds: string[] = [];
    threadsData: Thread[] = [];
    keyword = signal('');

    userId$ = this.auth.user$.pipe(
        filter((user): user is NonNullable<typeof user> => user !== null),
        map(user => user.id),
        shareReplay(1)
    );

    threads = new RefreshableResult({
        source: this._activatedRoute.params.pipe(
            switchMap(() => this._messageService.getAllMessages().pipe(toResult()))
        )
    });

    threads$ = this.threads.result$.pipe(
        tap(messages => {
           if(selectedThread() !== null){
               const updatedThread = (messages.value??[]).find(thread => thread.id === selectedThread()?.id);
               if(updatedThread !== undefined){
                   selectedThread.set(updatedThread);
               }
           }
        }),
        successfulResult()
    );

    filteredThreads$ = combineLatest([
        this.threads$,
        toObservable(this.keyword)
    ]).pipe(
        map(([threads, keyword]) => {
            const term = keyword.trim().toLowerCase();
            if (!term) {
 return threads; 
}
            return threads.filter((thread: Thread) =>
                thread.subject?.toLowerCase().includes(term) ||
                thread.messages?.some((m: Message) => m.content?.toLowerCase().includes(term))
            );
        })
    );

    changeParticipantRequest = new SendableRequest<ChangeParticipantPresenceResponse, ChangeParticipantPresence>({
        request: (payload) => {
            return this._messageService.addRemoveParticipant(payload!);
        },

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Participant presence has changed successfully.'
            });
            this.threads.refresh();
        }
    });

    ngOnInit() {
        this.threads.result$.pipe(
          map(r => r.value ?? [])
        ).subscribe(data => {
          this.threadsData = data;
        });
      }

    createNewMessage() {
        return CreateMessageComponent.show(this._dialog).closed.pipe(
            take(1),
            tap(() => {
                this.threads.refresh();
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    addRemoveParticipant(addRemoveParticipant: ChangeParticipantPresence) {
        this.changeParticipantRequest.send(addRemoveParticipant);
    }

    onThreadClicked(thread: Thread) {
        if(selectedThread() !== null){
            if(selectedThread()?.id === thread.id){
                selectedThread.set(null);
                return;
            }
        }
        selectedThread.set(thread);
    }

    addReply() {
        this.threads.refresh();
    }

    onSelectAllChange(event: Event) {
        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
          this.selectedThreadIds = this.threadsData.map(thread => thread.id);
        } else {
          this.selectedThreadIds = [];
        }
      }

      onCheckboxChange(event: { id: string; checked: boolean }) {
        if (event.checked) {
          if (!this.selectedThreadIds.includes(event.id)) {
            this.selectedThreadIds.push(event.id);
          }
        } else {
          this.selectedThreadIds = this.selectedThreadIds.filter(id => id !== event.id);
        }
      }

      areAllSelected(): boolean {
        return this.threadsData.length > 0 &&
               this.threadsData.every(thread => this.selectedThreadIds.includes(thread.id));
      }
      
      clearSelectedThreads() {
        this.selectedThreadIds = [];
      }

      
      
}
